import { useCallback } from 'react';
import { getInitialAllocatedMinutes } from 'api/resource-management';
import {
  FetchableResourceType,
  ResourceType,
  RoleIdentifier,
} from 'api/resource-management/types';
import { TeamReplacementProps } from 'api/teams';
import { useIsCurrentUserCollaborator } from 'hooks/permissions';
import { INVALID_ROLE_ID } from '../utils/common';
import { useAggregationManager } from './aggregation-manager';
import { useAddRemoveResource } from './resource-manager';
import { useAllocationAndSummaryManager } from './resource-summary-manager';
import { ManagerPropsWithLoadedRange } from './types';
import { useTotalAllocationInUIManager } from './ui-total-allocation-manager';

export const useReassignAllocationManager = (
  props: ManagerPropsWithLoadedRange,
): {
  reassignAllocationsFromUserToTeam: (props: TeamReplacementProps) => void;
} => {
  const { schedulerRef, teamLookup, userLookup, rangeLoadedInUI, roleLookup } =
    props;
  const isCollaborator = useIsCurrentUserCollaborator();
  const { addResource, removeResource } = useAddRemoveResource(props);
  const { updateAllocationAndSummary } = useAllocationAndSummaryManager(props);
  const { setTotalAllocationForResource } =
    useTotalAllocationInUIManager(props);
  const {
    getAllocatedMinutesFromAggregation,
    setAggregationForResourceInProject,
  } = useAggregationManager();

  const reassignAllocationsFromUserToTeam = useCallback(
    ({ projectId, userId, teamId, roleId }: TeamReplacementProps): void => {
      schedulerRef.current.instance.suspendRefresh();
      if (!isCollaborator) {
        addResource({
          projectId,
          resource: {
            type: ResourceType.Team,
            team: teamLookup[teamId],
          },
        });
      }

      const team: RoleIdentifier = {
        type: ResourceType.Role,
        teamId,
        roleId: teamLookup[teamId].roleId ?? INVALID_ROLE_ID,
      };

      updateAllocationAndSummary(
        {
          [FetchableResourceType.UserList]: [userId],
          [FetchableResourceType.RoleList]: [
            teamLookup[teamId].roleId ?? INVALID_ROLE_ID,
          ],
        },
        rangeLoadedInUI,
      ).then(() => {
        setTotalAllocationForResource(
          projectId,
          team,
          getAllocatedMinutesFromAggregation(projectId, team)
            .totalAllocatedMinutes,
        );

        removeResource(projectId, {
          type: ResourceType.User,
          user: userLookup[userId],
          role: roleId != null ? roleLookup[roleId] : undefined,
          referenceTeam: teamLookup[teamId],
        });

        setAggregationForResourceInProject(
          projectId,
          {
            type: ResourceType.User,
            userId,
            roleId,
            referenceTeamId: teamId,
          },
          getInitialAllocatedMinutes(),
        );
      });
      schedulerRef.current.instance.resumeRefresh(true);
    },
    [
      addResource,
      getAllocatedMinutesFromAggregation,
      isCollaborator,
      rangeLoadedInUI,
      removeResource,
      roleLookup,
      schedulerRef,
      setAggregationForResourceInProject,
      setTotalAllocationForResource,
      teamLookup,
      updateAllocationAndSummary,
      userLookup,
    ],
  );

  return {
    reassignAllocationsFromUserToTeam,
  };
};
